/**
 * WeaveIn.ai Platform - Smart Forms API Service
 * TypeScript service layer for dynamic form templates and instances
 */

import axios, { AxiosInstance } from 'axios';
import { applyUiLanguageHeader } from '@/services/uiLanguageHeader';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'time'
  | 'datetime'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'file'
  | 'signature'
  | 'section_header'
  | 'rich_text'
  | 'divider'
  | 'spacer'
  | 'field_group'
  | 'billing_evaluator';

/**
 * billing_evaluator field config (extra_config):
 *   {
 *     mode: 'doc_completeness' | 'denial_score' | 'auto_coder',
 *     program_type?: 'ECM' | 'CS' | 'CHW' | 'Doula',  // doc_completeness only
 *     note_field_key?: string,                          // doc_completeness/auto_coder: which field carries the note
 *     encounter_field_key?: string,                     // denial_score: which field carries encounter_id
 *     program_id_field_key?: string,                    // auto_coder
 *     use_llm?: boolean,
 *   }
 */

export type FormType =
  | 'assessment'
  | 'progress_note'
  | 'intake'
  | 'consent'
  | 'care_plan'
  | 'billing'
  | 'custom'
  // Activity-tracking form types — used by the Somos / FUERTES workstream
  // and any CBO running aggregate outreach + Hub Meeting / Training
  // attendance. event_detail wraps a calendar_events row; activity_log
  // stands alone (anonymous outreach with no parent FKs).
  | 'event_detail'
  | 'activity_log';

export type FormInstanceStatus = 'draft' | 'submitted' | 'reviewed' | 'approved';

/**
 * Field widths, as twelfths of a section row. Named rather than numeric so the
 * builder offers a short, meaningful menu instead of a 1-12 spinner, and so the
 * set can grow without migrating stored templates.
 */
export type FieldWidth = 'full' | 'two_thirds' | 'half' | 'third' | 'quarter';

export interface FormFieldSchema {
  field_id: string;
  field_type: FieldType;
  label: string;
  description?: string;
  required: boolean;
  // Historically stored as string[]; the form builder now writes
  // {label, value} objects. Renderers must normalize via fieldOptions.ts.
  options?: Array<string | { label: string; value: string }>;
  validation?: Record<string, any>;
  /**
   * @deprecated Legacy per-form conditional field from the original monorepo.
   * Nothing evaluates it — field configuration is the single source of truth
   * for conditional logic via `visibility_rules` below. Kept only so old
   * snapshots that still carry the key round-trip without loss; do not write
   * new values here.
   */
  conditional_logic?: Record<string, any>;
  // Conditional visibility carried from com_fields.visibility_rules when the
  // field is imported from the master field config. Evaluated client-side by
  // the shared fieldVisibility util (FormInstanceEditor / FormRenderer /
  // PublicFormPage) to hide the field until its conditions are met. Same shape
  // as members.ts VisibilityRules.
  /**
   * This FORM's own conditional rule for the field, overriding the shared one
   * from field configuration. Undefined/null means inherit — an override with
   * an EMPTY conditions list is a real answer meaning "always show here".
   * Resolved by effectiveRules() in fieldVisibility.ts, and by the backend on
   * read. Reverting is deleting the key, not copying the shared rule in.
   */
  visibility_rules_override?: {
    logic: 'AND' | 'OR';
    conditions: Array<{ field: string; operator: string; value?: any }>;
    when_hidden?: 'hide' | 'disable';
  } | null;
  visibility_rules?: {
    logic: 'AND' | 'OR';
    conditions: Array<{ field: string; operator: string; value?: any }>;
    when_hidden?: 'hide' | 'disable';
  } | null;
  default_value?: any;
  placeholder?: string;
  help_text?: string;
  /**
   * Whether the field offers a blank first choice, so an answer can be left
   * empty. The backend schema used to drop keys it did not declare, which is
   * how 159 of IFR's dropdowns lost theirs on a single save (TRUS-621).
   */
  allow_blank?: boolean;
  order: number;
  /**
   * Relative width of this field within its section's row (TRUS-545 Phase 2).
   * Undefined means full, which is how every form rendered before multi-column
   * layout existed, so untouched templates keep their single-column stack.
   * Widths collapse to full below the `sm` breakpoint — a two-column form on a
   * phone is a one-column form.
   */
  width?: FieldWidth;
  // Data source for auto-population from member data
  // Format: "member.field_name" for core fields (e.g., "member.first_name", "member.date_of_birth")
  // Format: "member.custom_fields.field_name" for custom fields
  data_source?: string;
  // Visual styling — applies to both layout elements and data fields
  style?: {
    variant?: 'info' | 'warning' | 'success' | 'error' | 'neutral';
    size?: 'sm' | 'md' | 'lg'; // spacer height, heading size
    alignment?: 'left' | 'center' | 'right';
    border_style?: 'none' | 'solid' | 'dashed' | 'thick';
    // Field-level visual emphasis (applies to imported data fields)
    highlight?: boolean;           // wrap field in a colored highlight box
    alert_note?: string;           // show alert banner above/below the field
    alert_position?: 'above' | 'below'; // where the alert note appears
  };
  /**
   * Fields carry keys this interface does not name — the backend now passes
   * them through rather than discarding them (extra="allow"), so the type
   * has to admit them or a round-trip looks lossless to TypeScript while
   * dropping data at runtime.
   */
  [key: string]: unknown;
}

export interface FormSectionSchema {
  section_id: string;
  title: string;
  description?: string;
  fields: FormFieldSchema[];
  order: number;
  collapsible: boolean;
  collapsed_by_default: boolean;
}

/** Who fills a form template. */
export type TargetEntity = 'member' | 'registered_user' | 'activity_event';

/** Known keys inside a form template's `settings` jsonb (TRUS-498). */
export interface FormTemplateSettings {
  /** Whether drafts auto-save while the form is being filled in. */
  auto_save?: boolean | null;
  /** Auto-save cadence in seconds. A user may override this for themselves. */
  auto_save_interval?: number | null;
  /**
   * A starter template the platform seeded, not something this organization
   * built. Samples arrive inactive: an admin ticks Active to adopt one.
   */
  is_sample?: boolean | null;
  /**
   * Refresh this form's questions from the organization's master field config.
   * Destructive — it replaces the form's own show-if rules (TRUS-621).
   */
  sync_with_field_config?: boolean | null;
}

export interface FormTemplateBase {
  name: string;
  description?: string;
  program_id?: number;
  form_type: FormType;
  /** Defaults to 'member' on create. Drives where the form surfaces and
   *  how form_instances are assigned. Member = enrollment-scoped (default).
   *  Registered user = no enrollment needed; assigned to users.id directly.
   *  Activity event = anonymous (no parent FK), used for Somos outreach +
   *  Hub Meeting / Training detail forms. */
  target_entity?: TargetEntity;
  sections: FormSectionSchema[];
  /** Free-form settings bag. Known keys include `auto_save` and `auto_save_interval`
   *  (seconds), which set this form's default auto-save cadence — see
   *  constants/formAutoSave.ts. Absent keys mean "use the platform default". */
  settings?: FormTemplateSettings & Record<string, any>;
  is_active?: boolean;
  /** Reusable visual theme. NULL -> org default design template at render. */
  design_template_id?: number | null;
}

export interface FormTemplateCreate extends FormTemplateBase {}

export interface FormTemplateUpdate {
  name?: string;
  description?: string;
  sections?: FormSectionSchema[];
  settings?: FormTemplateSettings & Record<string, any>;
  is_active?: boolean;
  target_entity?: TargetEntity;
  design_template_id?: number | null;
}

export interface NeedSuggestion {
  id: number;
  source: 'mif' | 'assessment';
  domain: string;
  rationale: Array<{ field?: string; value?: string }>;
  source_ref?: string | null;
  status: string;
}

export interface FormTemplate extends FormTemplateBase {
  id: number;
  cbo_id: number;
  is_active: boolean;
  version: number;
  created_by_user_id: number;
  created_ts: string;
  last_updated_ts: string;
  program_name?: string;
  created_by_name?: string;
  /** Set by GET /by-program/{id} only: form_template_programs.is_required for that program. */
  is_required?: boolean;
  /** Set by GET /by-program/{id} only: form_template_programs.display_order. */
  display_order?: number;
}

/**
 * The list endpoints (`/templates/`, `/instances/`) page at 50 rows by default
 * and cap `page_size` at 100. A caller that asks once and stops sees at most
 * the first page with no hint that more exist — a member with 60 notes simply
 * "has" 50. This walks pages until a short one comes back.
 */
export const LIST_MAX_PAGE_SIZE = 100;

export async function fetchAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<T[]>,
  pageSize: number = LIST_MAX_PAGE_SIZE,
  maxPages: number = 50,
): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const rows = await fetchPage(page, pageSize);
    const list = Array.isArray(rows) ? rows : [];
    out.push(...list);
    if (list.length < pageSize) break;
  }
  return out;
}

export interface FormTemplateListResponse {
  templates: FormTemplate[];
  total: number;
  page: number;
  page_size: number;
}

export interface FormInstanceBase {
  form_template_id: number;
  enrollment_id?: number;
  household_id?: number;
  workforce_case_id?: number;
  workflow_step_id?: number;
  workflow_instance_id?: number;
  /** Link this instance to a calendar_events row (event-detail forms — Hub
   *  Meeting / Training-Workshop). NULL for ordinary instances. Added with
   *  Somos Phase 1 (backend migration 081 + forms.py FormInstanceCreate). */
  calendar_event_id?: number;
  assigned_to_user_id?: number;
  due_date?: string;
  /** TRUS-762: file the instance under this case; absent = derived from the enrollment. */
  case_episode_id?: number;
}

export interface FormInstanceCreate extends FormInstanceBase {
  /** Values for the template's `input` variables (fields with data_source
   *  "variable.<key>"), entered by staff when the form starts (BE #1220). */
  variable_inputs?: Record<string, string>;
}

export interface FormSubmissionData {
  field_data: Record<string, any>;
  status: FormInstanceStatus;
  notes?: string;
  /**
   * Why an already-submitted instance is being changed. Recorded on the
   * form_instance_history row so an amended progress note carries a reason, not just a
   * diff. Ignored for drafts. Requires the org's `forms.amend_submitted` grant.
   */
  amendment_reason?: string;
}

/** Org-granted function permissions the current user holds (see /api/forms/my-permissions). */
/** Statuses meaning the note has been billed to a managed care plan (TRUS-362). */
export const BILLED_STATUSES = ['billed', 'billed_qa'] as const;

/**
 * What it takes to edit an instance in this status — mirrors the backend's
 * `amend_requirements` so the UI and the API cannot disagree about editability.
 * Returns null when the record is freely editable (a draft).
 */
export function amendRequirements(status?: string | null):
  | { function: 'forms.amend_submitted' | 'forms.amend_billed'; reasonRequired: boolean }
  | null {
  const s = (status || '').trim().toLowerCase();
  if ((BILLED_STATUSES as readonly string[]).includes(s)) {
    // Restricted after billing; editable during QA, with the audit note captured.
    return { function: 'forms.amend_billed', reasonRequired: true };
  }
  if (['submitted', 'reviewed', 'approved'].includes(s)) {
    // Required here too now. It began optional and defaulted to the literal string
    // "Amended after submission" — a restatement of the status, not a reason, which left the
    // history trail unable to answer why a signed record changed.
    return { function: 'forms.amend_submitted', reasonRequired: true };
  }
  return null;
}

export interface MyFormPermissions {
  organization_id: number;
  functions: string[];
  can_amend_submitted: boolean;
  /** Stronger grant: edit a note that has already been billed to a plan. */
  can_amend_billed?: boolean;
  /** Move a note through the billing cycle (Billed / Billed + QA). */
  can_set_billing_status?: boolean;
}

export interface FormInstance extends FormInstanceBase {
  id: number;
  cbo_id: number;
  form_template_name: string;
  form_type?: FormType;
  workflow_instance_id?: number;
  status: FormInstanceStatus;
  field_data: Record<string, any>;
  version: number;
  started_at?: string;
  submitted_at?: string;
  reviewed_at?: string;
  created_ts: string;
  last_updated_ts: string;
  member_name?: string;
  assigned_to_name?: string;
  completion_percentage?: number;
}

export interface FormInstanceListResponse {
  instances: FormInstance[];
  total: number;
  page: number;
  page_size: number;
}

export interface FormProgramAssociation {
  id: number;
  form_template_id: number;
  program_id: number;
  program_name?: string;
  is_required: boolean;
  display_order: number;
  created_ts: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class FormsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      // TRUS-446 — form template labels, section titles and picklist option
      // labels are resolved server-side, and this header is what selects the
      // language. Without it the API falls back to the saved user preference
      // and serves English, which is how Somos got Spanish chrome around
      // English form labels despite having every string translated.
      applyUiLanguageHeader(config);

      return config;
    });
  }

  // ========================================================================
  // FORM TEMPLATE ENDPOINTS
  // ========================================================================

  async createFormTemplate(data: FormTemplateCreate): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>('/api/forms/templates/', data);
    return response.data;
  }

  // --- Need suggestions (TRUS-576) ------------------------------------------
  // A suggestion is evidence, not a need: it becomes an Identified Need only
  // when an LCM accepts it, and that conversion is the tracked action.

  async listNeedSuggestions(memberId: number, status?: string): Promise<NeedSuggestion[]> {
    const response = await this.client.get<NeedSuggestion[]>('/api/forms/need-suggestions', {
      params: { member_id: memberId, ...(status ? { status_filter: status } : {}) },
    });
    return response.data;
  }

  async acceptNeedSuggestion(id: number, carePlanInstanceId: number): Promise<any> {
    const response = await this.client.post(`/api/forms/need-suggestions/${id}/accept`, {
      care_plan_instance_id: carePlanInstanceId,
    });
    return response.data;
  }

  /** Permanent — no later MIF file re-raises a dismissed suggestion, which is
   *  why the API requires a reason. */
  async dismissNeedSuggestion(id: number, reason: string): Promise<any> {
    const response = await this.client.post(`/api/forms/need-suggestions/${id}/dismiss`, { reason });
    return response.data;
  }

  async listFormTemplates(params?: {
    program_id?: number;
    form_type?: FormType;
    is_active?: boolean;
    /**
     * Include templates that are not active. The list collapses to one row per
     * form name via `MAX(id) ... WHERE is_active = true`, so without this an
     * inactive template cannot be returned at all — `is_active: false` intersects
     * that subquery and comes back empty. Admin surfaces set it to see drafts.
     */
    include_drafts?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<FormTemplate[]> {
    const response = await this.client.get<FormTemplate[]>('/api/forms/templates/', { params });
    return response.data;
  }

  async getFormTemplate(templateId: number, version?: number): Promise<FormTemplate> {
    const response = await this.client.get<FormTemplate>(`/api/forms/templates/${templateId}`, {
      params: version ? { version } : undefined,
    });
    return response.data;
  }

  async updateFormTemplate(
    templateId: number,
    data: FormTemplateUpdate,
    createNewVersion: boolean = false,
    // Turning the master field sync ON replaces a form's own show-if rules.
    // The backend answers 409 unless this says the user was asked (TRUS-621).
    enableFieldSync: boolean = false
  ): Promise<FormTemplate> {
    const response = await this.client.put<FormTemplate>(`/api/forms/templates/${templateId}`, data, {
      params: { create_new_version: createNewVersion, enable_field_sync: enableFieldSync },
    });
    return response.data;
  }

  async deleteFormTemplate(templateId: number): Promise<void> {
    await this.client.delete(`/api/forms/templates/${templateId}`);
  }

  async getFormTemplateVersions(templateId: number): Promise<FormTemplate[]> {
    const response = await this.client.get<FormTemplate[]>(`/api/forms/templates/${templateId}/versions`);
    return response.data;
  }

  // ========================================================================
  // FORM-PROGRAM ASSOCIATION ENDPOINTS
  // ========================================================================

  async getFormTemplatePrograms(templateId: number): Promise<FormProgramAssociation[]> {
    const response = await this.client.get<FormProgramAssociation[]>(`/api/forms/templates/${templateId}/programs`);
    return response.data;
  }

  async addFormTemplateProgram(
    templateId: number,
    data: { program_id: number; is_required?: boolean; display_order?: number }
  ): Promise<FormProgramAssociation> {
    const response = await this.client.post<FormProgramAssociation>(
      `/api/forms/templates/${templateId}/programs`,
      data
    );
    return response.data;
  }

  async removeFormTemplateProgram(templateId: number, programId: number): Promise<void> {
    await this.client.delete(`/api/forms/templates/${templateId}/programs/${programId}`);
  }

  async getFormsByProgram(
    programId: number,
    includeGlobal: boolean = true
  ): Promise<FormTemplate[]> {
    const response = await this.client.get<FormTemplate[]>(`/api/forms/by-program/${programId}`, {
      params: { include_global: includeGlobal },
    });
    return response.data;
  }

  // ========================================================================
  // FORM INSTANCE ENDPOINTS
  // ========================================================================

  async createFormInstance(data: FormInstanceCreate): Promise<FormInstance> {
    const response = await this.client.post<FormInstance>('/api/forms/instances/', data);
    return response.data;
  }

  async listFormInstances(params?: {
    enrollment_id?: number;
    household_id?: number;
    form_template_id?: number;
    status?: FormInstanceStatus;
    assigned_to_user_id?: number;
    /** True → return only instances assigned to the caller (BE PR #52). */
    mine?: boolean;
    /** Filter by target_entity from the form template (BE PR #52). */
    target_entity?: TargetEntity;
    page?: number;
    page_size?: number;
  }): Promise<FormInstance[]> {
    const response = await this.client.get<FormInstance[]>('/api/forms/instances/', { params });
    return response.data;
  }

  async getFormInstance(instanceId: number): Promise<FormInstance> {
    const response = await this.client.get<FormInstance>(`/api/forms/instances/${instanceId}`);
    return response.data;
  }

  /**
   * Every form for a member across their enrollments, plus the public form
   * submission that created the record (no enrollment; TRUS-636). Read-only —
   * unlike listFormInstances({ enrollment_id }), it never creates instances.
   */
  async listMemberFormInstances(memberId: number): Promise<FormInstance[]> {
    const response = await this.client.get<FormInstance[]>(`/api/forms/instances/by-member/${memberId}`);
    return response.data;
  }

  async submitFormInstance(instanceId: number, data: FormSubmissionData): Promise<any> {
    const response = await this.client.put(`/api/forms/instances/${instanceId}/submit`, data);
    return response.data;
  }

  /**
   * Which org-granted functions the caller holds — e.g. whether they may amend a
   * submitted record. Per-org grant, so the client cannot infer it from a role name.
   */
  async getMyFormPermissions(): Promise<MyFormPermissions> {
    const response = await this.client.get<MyFormPermissions>('/api/forms/my-permissions');
    return response.data;
  }

  /**
   * Move a note through the MCP billing cycle without touching a clinical field, so
   * billing staff can mark a batch reported. Gated on `forms.set_billing_status`.
   */
  async setBillingStatus(
    instanceId: number,
    status: 'billed' | 'billed_qa' | 'submitted',
    note?: string,
  ): Promise<{ id: number; status: string; previous_status: string }> {
    const response = await this.client.put(`/api/forms/instances/${instanceId}/billing-status`, {
      status,
      note,
    });
    return response.data;
  }

  /**
   * TRUS-763: sign a form that is already submitted. Adds only the signature and its
   * record (server-stamped date and signer); no submit side effects re-run. Refused
   * (409) when the field already holds a signature: replacing one is an amendment.
   */
  async addSignature(
    instanceId: number,
    body: {
      field_id: string;
      signature_data: string;
      signer_name?: string;
      signature_type?: string;
      consent_accepted?: boolean;
      signed_local_date?: string;
    },
  ): Promise<{
    instance_id: number;
    field_id: string;
    signature: {
      signer_name?: string | null;
      signature_type?: string | null;
      consent_accepted?: boolean;
      signed_at: string;
      signed_on: string;
      signed_by_user_id: number | null;
      added_after_submit?: boolean;
    };
    date_field_id: string | null;
    status: string;
  }> {
    const response = await this.client.post(`/api/forms/instances/${instanceId}/signatures`, body);
    return response.data;
  }

  async getFormInstanceHistory(instanceId: number): Promise<any[]> {
    const response = await this.client.get<any[]>(`/api/forms/instances/${instanceId}/history`);
    return response.data;
  }

  async deleteFormInstance(instanceId: number): Promise<void> {
    await this.client.delete(`/api/forms/instances/${instanceId}`);
  }

  // ========================================================================
  // UTILITY ENDPOINTS
  // ========================================================================

  async getAvailableFieldTypes(): Promise<Record<string, any>> {
    const response = await this.client.get<Record<string, any>>('/api/forms/field-types');
    return response.data;
  }

  async previewFormTemplate(templateId: number): Promise<any> {
    const response = await this.client.get(`/api/forms/templates/${templateId}/preview`);
    return response.data;
  }

  // ========================================================================
  // TEMPLATE CLONING (admin-only)
  // ========================================================================

  async cloneFormTemplate(
    templateId: number,
    targetCboId: number,
    newName?: string
  ): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>(
      `/api/forms/templates/${templateId}/clone`,
      { target_cbo_id: targetCboId, new_name: newName }
    );
    return response.data;
  }

  async bulkCloneFormTemplates(
    sourceCboId: number,
    templateIds: number[],
    targetCboId: number
  ): Promise<{ cloned_count: number; error_count: number; cloned: any[]; errors: any[] }> {
    const response = await this.client.post('/api/forms/templates/bulk-clone', {
      source_cbo_id: sourceCboId,
      template_ids: templateIds,
      target_cbo_id: targetCboId,
    });
    return response.data;
  }

  async listTemplatesAvailableForClone(
    sourceCboId: number
  ): Promise<{ source_organization: { id: number; name: string }; templates: any[] }> {
    const response = await this.client.get('/api/forms/templates/available-for-clone', {
      params: { source_cbo_id: sourceCboId },
    });
    return response.data;
  }
}

// Export singleton instance
export const formsAPI = new FormsAPI();

// ============================================================================
// REACT HOOKS
// ============================================================================

export { default as useFormTemplates } from '@/hooks/useFormTemplates';
export { default as useFormInstances } from '@/hooks/useFormInstances';
